// API client functions for tenant branding endpoints.

import type { ApiClient } from '@/lib/api/client'

// --- Types ---

export interface BrandingColors {
  headerBackground?: string
  headerForeground?: string
  sidebarBackground?: string
  sidebarForeground?: string
  footerBackground?: string
  footerForeground?: string
  background?: string
  foreground?: string
  bodyForeground?: string
  border?: string
  card?: string
  cardForeground?: string
  cardHeaderForeground?: string
  buttonBackground?: string
  buttonForeground?: string
  gridAppetiteIn?: string
  gridAppetiteOut?: string
  gridNodeInherent?: string
  gridNodeResidual?: string
  gridNodeTarget?: string
}

export interface BrandingResponse {
  colors: BrandingColors
  fontId: string
  hasLogo: boolean
  updatedAt?: string
}

export interface UpdateBrandingRequest {
  colors?: BrandingColors
  fontId?: string
}

// Supported fonts — keep in sync with backend validFontIDs and CSS @font-face declarations.
export const SUPPORTED_FONTS = [
  { id: 'inter', name: 'Inter', family: "'Inter', sans-serif", category: 'Sans-serif' },
  { id: 'dm-sans', name: 'DM Sans', family: "'DM Sans', sans-serif", category: 'Sans-serif' },
  {
    id: 'source-sans',
    name: 'Source Sans 3',
    family: "'Source Sans 3', sans-serif",
    category: 'Sans-serif',
  },
  { id: 'merriweather', name: 'Merriweather', family: "'Merriweather', serif", category: 'Serif' },
  {
    id: 'jetbrains-mono',
    name: 'JetBrains Mono',
    family: "'JetBrains Mono', monospace",
    category: 'Monospace',
  },
  {
    id: 'space-grotesk',
    name: 'Space Grotesk',
    family: "'Space Grotesk', sans-serif",
    category: 'Console',
  },
] as const

// --- API functions ---

export function brandingApi(api: ApiClient) {
  return {
    get: (): Promise<BrandingResponse> => api.get('/settings/branding'),

    update: (data: UpdateBrandingRequest): Promise<BrandingResponse> =>
      api.put('/settings/branding', data),

    uploadLogo: async (file: File): Promise<void> => {
      const buffer = await file.arrayBuffer()
      await api.fetch('/branding/logo', {
        method: 'PUT',
        headers: { 'Content-Type': 'image/png' },
        body: buffer,
      })
    },

    deleteLogo: (): Promise<void> => api.del('/branding/logo'),
  }
}
